import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLongArrowAltRight,
  faCheckCircle,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import AssetService from '../../Services/AssetService';
import IconsContext from '../../Context/IconsContext';

export default memo(GatewaySwapModal);

const initState = {
  error: '',
  provisionID: '',
  serialNumber: '',
  requestPending: false,
  swapInProgress: false,
};

function GatewaySwapModal(props) {
  const [state, updateState] = useState(initState);

  const { signalIconWhite } = useContext(IconsContext);

  const service = useMemo(() => new AssetService(), []);
  const source = CancelToken.source();

  const handleExitAfterSwap = () => {
    if (!state.swapInProgress) return;

    const assetUpdate = {
      mac: state.serialNumber,
      provision: { _id: state.provisionID },
    };
    props.handleGatewaySwap(assetUpdate);
  };

  const swapGateway = async () => {
    try {
      updateState((prevState) => ({ ...prevState, requestPending: true }));
      const body = [
        {
          autoTz: props.asset.autoTz,
          gatewayName: props.asset.nickname,
          gatewayId: props.asset._id,
          remote: props.asset.remote,
          serialNumber: state.serialNumber,
          templateID: props.asset.gtemplateID,
          timezone: props.asset.timezone,
          type: 'gateway_swap',
          vendor: props.asset.vendor,
        },
      ];
      const {
        results: [{ _id: provisionID }],
      } = await service.ProvisionAssetBulk(body, props.asset.orgID);
      updateState((prevState) => ({
        ...prevState,
        provisionID,
        requestPending: false,
        swapInProgress: true,
      }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  };

  useEffect(() => source.cancel, []);

  return (
    <Modal
      backdrop={!state.swapInProgress || 'static'}
      centered
      isOpen={props.isOpen}
      toggle={() => {
        state.swapInProgress ? () => null : props.toggleModal();
      }}
    >
      <ModalHeader
        className="border-bottom-0 pb-0"
        toggle={() => {
          state.swapInProgress ? () => null : props.toggleModal();
        }}
      >
        Swap Gateway
      </ModalHeader>
      <ModalBody className="py-0">
        {state.swapInProgress ? (
          <div className="container-fluid my-4">
            <div className="row align-items-stretch justify-content-center">
              <div className="col-5 d-flex flex-column align-items-center">
                <div className="rounded text-dodger-blue bg-gradient-first-blue px-4">
                  <img
                    src={signalIconWhite}
                    alt="signal_icon"
                    style={{ height: '70px' }}
                  />
                </div>
                <p className="text-muted mt-3 mb-0 font-weight-normal">
                  Next Step
                </p>
                <p className="text-muted mt-0 font-weight-normal">
                  Turn on Gateway
                </p>
              </div>
              <div className="col-1 d-flex align-items-center justify-content-center">
                <FontAwesomeIcon
                  className="text-light-gray-3"
                  icon={faLongArrowAltRight}
                  size="3x"
                />
              </div>
              <div className="col-5 d-flex flex-column align-items-center">
                <FontAwesomeIcon
                  className="text-icon-link-gray"
                  icon={faCheckCircle}
                  size="4x"
                  style={{ height: '70px' }}
                />
                <p className="nine-tenth-text text-third-gray mt-4">
                  Gateway Swapped
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="modal-supplemental-text three-quarter-text">
              Swap a new gateway for this machine by adding the new
              gateway&#39;s serial number.
            </p>
            <div className="form-group mr-4 mb-5">
              <label
                className="modal-inputlabel-text font-weight-bold"
                htmlFor="serialNumber"
              >
                New Gateway Serial Number
              </label>
              <input
                className="form-control form-control-sm rounded-lg"
                disabled={state.requestPending || state.swapInProgress}
                name="serialNumber"
                onChange={({ target: { value: serialNumber } }) =>
                  updateState((prevState) => ({ ...prevState, serialNumber }))
                }
                type="text"
                value={state.serialNumber}
              />
            </div>
          </>
        )}
        <p className="mb-0 text-right three-quarter-text text-danger">
          {state.error}
        </p>
      </ModalBody>
      <ModalFooter className="border-top-0 pt-0">
        {state.swapInProgress ? (
          <button
            className="btn btn-outline-secondary px-3 px-lg-4 py-0 nine-tenth-text"
            onClick={handleExitAfterSwap}
          >
            Close
          </button>
        ) : (
          <>
            <button
              className="btn btn-outline-secondary px-3 px-lg-4 py-0 nine-tenth-text"
              onClick={props.toggleModal}
            >
              Cancel
            </button>
            <button
              className="btn modal-button-submit nine-tenth-text px-2 px-lg-4 py-0 ml-3"
              onClick={swapGateway}
              disabled={state.requestPending}
            >
              {state.requestPending ? (
                <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
              ) : (
                'Swap'
              )}
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

GatewaySwapModal.propTypes = {
  asset: PropTypes.object,
  handleGatewaySwap: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};
